interface Step {
  /**
   * Step number
   */
  number: number;

  /**
   * Step title
   */
  title: string;

  /**
   * Step description
   */
  description: string;
}

interface HowItWorksSectionProps {
  /**
   * Section title
   */
  title?: string;

  /**
   * Section description
   */
  description?: string;

  /**
   * Array of steps
   */
  steps?: Step[];

  /**
   * Additional CSS classes
   */
  className?: string;
}

const defaultSteps: Step[] = [
  {
    number: 1,
    title: "Sign up and choose a cohort",
    description: "Select a cohort that fits your schedule and join a community of founders"
  },
  {
    number: 2,
    title: "Watch videos and complete quizzes",
    description: "Learn customer discovery fundamentals through structured video modules"
  },
  {
    number: 3,
    title: "Log your customer interviews",
    description: "Track your validation progress and document insights from real conversations"
  },
  {
    number: 4,
    title: "Get feedback from coaches",
    description: "Receive personalized guidance from experienced founders in 1-on-1 sessions"
  }
]

export function HowItWorksSection({
  title = "How It Works",
  description = "A simple, proven process to validate your startup idea",
  steps = defaultSteps,
  className = ""
}: HowItWorksSectionProps) {
  return (
    <section className={`bg-background py-16 md:py-24 ${className}`}>
      <div className="container mx-auto px-4 md:px-6">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-12 md:mb-16">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-4">
            {title}
          </h2>
          <p className="text-lg text-muted-foreground">
            {description}
          </p>
        </div>

        {/* Steps */}
        <div className="max-w-4xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-12">
            {steps.map((step, index) => (
              <div
                key={index}
                className="relative"
              >
                {/* Connector Line (desktop only, not for last item in column) */}
                {index < steps.length - 2 && (
                  <div className="hidden md:block absolute left-8 top-20 w-0.5 h-full bg-border" />
                )}

                <div className="flex gap-6">
                  {/* Number Circle */}
                  <div className="flex-shrink-0">
                    <div className="w-16 h-16 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-2xl font-bold shadow-lg">
                      {step.number}
                    </div>
                  </div>

                  {/* Content */}
                  <div className="flex-1 pt-2">
                    <h3 className="text-xl font-semibold text-foreground mb-2">
                      {step.title}
                    </h3>
                    <p className="text-muted-foreground leading-relaxed">
                      {step.description}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  )
}
